import { useMemo } from 'react';
import { useQuery, UseQueryResult } from 'react-query';
import {
  TemplateInsightAPI,
  TemplateInsightGroupBy,
  TemplateInsightResponses,
} from 'api/template-insight';
import { Filter } from 'components/molecules/filters/types';
import { DateRange } from 'components/organisms/projects-report/common/range-picker/types';
import { FieldFilter } from 'components/organisms/tasks/filters';

const TEMPLATE_INSIGHT_QUERY = 'TEMPLATE_INSIGHT_QUERY';

type TemplateInsightPayload<T extends TemplateInsightGroupBy> = {
  groupBy: T;
  range: DateRange;
  filter: Filter;
};

const getTemplateInsightQueryKey = <T extends TemplateInsightGroupBy>({
  groupBy,
  range,
  filter,
}: TemplateInsightPayload<T>) => [
  TEMPLATE_INSIGHT_QUERY,
  groupBy,
  range,
  JSON.stringify({ filter }),
];

export const useTemplateInsightQuery = <T extends TemplateInsightGroupBy>({
  filter: { criteria, type },
  ...rest
}: TemplateInsightPayload<T>): UseQueryResult<TemplateInsightResponses<T>> => {
  const validFilters = useMemo(
    () => criteria.filter(FieldFilter.isValid),
    [criteria],
  );

  return useQuery<TemplateInsightResponses<T>>({
    queryKey: getTemplateInsightQueryKey({
      filter: { criteria: validFilters, type },
      ...rest,
    }),
    queryFn: () =>
      TemplateInsightAPI.getTemplateReport({
        filter: { criteria: validFilters, type },
        ...rest,
      }),
  });
};
